import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Alert,
    CircularProgress,
    Chip,
    LinearProgress,
    Typography,
    Paper,
    alpha
} from '@mui/material';
import {
    Code as CodeIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Schedule as ScheduleIcon,
    Loop as LoopIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { sandboxApi } from '../../services/api';
import { useEnvironmentStatus } from '../../hooks/useEnvironmentStatus';
import { useAuthStore } from '../../stores';

const SandboxAssignment = ({ assignment, hideContainer = false, compact = false }) => {
    const { t } = useTranslation();
    const { token } = useAuthStore();
    const [isDeploying, setIsDeploying] = useState(false);
    const [error, setError] = useState(null);
    const intervalRef = useRef(null);

    const {
        environment,
        isCreating,
        isActive,
        isDestroyed,
        isFailed,
        refresh: refreshStatus
    } = useEnvironmentStatus(assignment?.id, token);

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const getStatusConfig = () => {
        if (isCreating) {
            return {
                label: t('sandbox_assignment.status.creating'),
                color: 'info',
                icon: <LoopIcon />,
                variant: 'outlined'
            };
        }
        if (isActive) {
            return {
                label: t('sandbox_assignment.status.active'),
                color: 'success',
                icon: <CheckCircleIcon />,
                variant: 'filled'
            };
        }
        if (isDestroyed) {
            return {
                label: t('sandbox_assignment.status.destroyed'),
                color: 'warning',
                icon: <RefreshIcon />,
                variant: 'outlined'
            };
        }
        if (isFailed) {
            return {
                label: t('sandbox_assignment.status.failed'),
                color: 'error',
                icon: <ErrorIcon />,
                variant: 'outlined'
            };
        }
        return null;
    };

    const calculateProgress = () => {
        if (!assignment?.max_attempts) return null;

        const attempts = environment?.recreation_count || 0;
        return {
            current: attempts,
            max: assignment.max_attempts,
            percentage: (attempts / assignment.max_attempts) * 100
        };
    };

    const calculateTimeRemaining = () => {
        if (!environment?.auto_destroy_at) return null;

        const now = new Date();
        const destroyAt = new Date(environment.auto_destroy_at);
        const diff = destroyAt - now;

        if (diff <= 0) return t('sandbox_assignment.time_expired');

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        return t('sandbox_assignment.time_remaining', { hours, minutes });
    };

    const handleOpenSandbox = async () => {
        setIsDeploying(true);
        setError(null);

        try {
            // If environment is active - just open it with assignment mode
            if (isActive && environment?.sandbox) {
                const url = `/sandbox/${environment.sandbox}?mode=assignment&assignment_id=${assignment.id}`;
                window.open(url, '_blank');
                setIsDeploying(false);
                return;
            }

            // If environment is creating - wait for it
            if (isCreating) {
                let attempts = 0;
                const maxAttempts = 60; // 3 minutes max

                intervalRef.current = setInterval(async () => {
                    attempts++;
                    if (attempts > maxAttempts) {
                        clearInterval(intervalRef.current);
                        setError(t('sandbox_assignment.errors.environment_creation_timeout'));
                        setIsDeploying(false);
                        return;
                    }

                    await refreshStatus();

                    if (environment?.status === 'active' && environment?.sandbox) {
                        clearInterval(intervalRef.current);
                        const url = `/sandbox/${environment.sandbox}?mode=assignment&assignment_id=${assignment.id}`;
                        window.open(url, '_blank');
                        setIsDeploying(false);
                    }
                }, 3000);
                return;
            }

            // Create new environment from template
            const templateId = assignment.environment_template?.id ||
                             assignment.sandbox_details?.template_id;

            if (!templateId) {
                throw new Error(t('sandbox_assignment.errors.no_template'));
            }

            const response = await sandboxApi.deployTemplate(
                templateId,
                assignment.id,
                token
            );

            // Wait for creation to complete
            let attempts = 0;
            const maxAttempts = 60;

            intervalRef.current = setInterval(async () => {
                try {
                    attempts++;
                    if (attempts > maxAttempts) {
                        clearInterval(intervalRef.current);
                        throw new Error(t('sandbox_assignment.errors.environment_creation_timeout'));
                    }

                    const [{ stage }] = await sandboxApi.getProcessStage(response.process_id, token);

                    if (stage >= 2) {
                        clearInterval(intervalRef.current);
                        const url = `/sandbox/${response.sandbox_id}?mode=assignment&assignment_id=${assignment.id}`;
                        window.open(url, '_blank');
                        setIsDeploying(false);
                        refreshStatus();
                    }
                } catch (error) {
                    clearInterval(intervalRef.current);
                    throw error;
                }
            }, 3000);

        } catch (error) {
            console.error('Error opening sandbox:', error);
            setError(error.message || t('sandbox_assignment.errors.environment_preparation_error'));
            setIsDeploying(false);
        }
    };

    const handleRecreate = async () => {
        setIsDeploying(true);
        setError(null);

        try {
            // Delete old environment and create new one
            const templateId = assignment.environment_template?.id ||
                             assignment.sandbox_details?.template_id;

            const response = await sandboxApi.deployTemplate(
                templateId,
                assignment.id,
                token
            );

            // Wait for creation
            let attempts = 0;
            const maxAttempts = 60;

            intervalRef.current = setInterval(async () => {
                try {
                    attempts++;
                    if (attempts > maxAttempts) {
                        clearInterval(intervalRef.current);
                        throw new Error(t('sandbox_assignment.errors.environment_creation_timeout'));
                    }

                    const [{ stage }] = await sandboxApi.getProcessStage(response.process_id, token);

                    if (stage >= 2) {
                        clearInterval(intervalRef.current);
                        const url = `/sandbox/${response.sandbox_id}?mode=assignment&assignment_id=${assignment.id}`;
                        window.open(url, '_blank');
                        setIsDeploying(false);
                        refreshStatus();
                    }
                } catch (error) {
                    clearInterval(intervalRef.current);
                    throw error;
                }
            }, 3000);

        } catch (error) {
            console.error('Error recreating environment:', error);
            setError(error.message || t('sandbox_assignment.errors.recreation_error'));
            setIsDeploying(false);
        }
    };

    const statusConfig = getStatusConfig();
    const progress = calculateProgress();
    const timeRemaining = calculateTimeRemaining();

    const content = (
        <Box>
            {/* Status Chip - hide in compact mode */}
            {!compact && statusConfig && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        icon={statusConfig.icon}
                        label={statusConfig.label}
                        color={statusConfig.color}
                        variant={statusConfig.variant}
                        size="small"
                    />
                    {isActive && timeRemaining && (
                        <Chip
                            icon={<ScheduleIcon />}
                            label={timeRemaining}
                            color="default"
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>
            )}

            {/* Attempts Progress - hide in compact mode */}
            {!compact && progress && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            {t('sandbox_assignment.attempts_used')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {progress.current} / {progress.max}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress.percentage}
                        color={progress.percentage >= 80 ? 'error' : 'primary'}
                        sx={{ height: 6, borderRadius: 1 }}
                    />
                </Box>
            )}

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
                {(isDestroyed || isFailed) ? (
                    // Show recreate button for destroyed/failed environments
                    <Button
                        variant="contained"
                        size={compact ? 'small' : 'medium'}
                        onClick={handleRecreate}
                        startIcon={isDeploying ? (
                            <CircularProgress size={compact ? 16 : 20} color="inherit" />
                        ) : (
                            <RefreshIcon size={compact ? 16 : 20} />
                        )}
                        disabled={
                            isDeploying ||
                            (!compact && progress && progress.current >= progress.max)
                        }
                        sx={{
                            bgcolor: 'warning.main',
                            '&:hover': {
                                bgcolor: 'warning.dark'
                            },
                            '&.Mui-disabled': {
                                bgcolor: alpha('#FF5722', 0.3)
                            }
                        }}
                    >
                        {isDeploying
                            ? t('sandbox_assignment.buttons.recreating')
                            : t('sandbox_assignment.buttons.recreate')}
                    </Button>
                ) : (
                    // Show open/create button for active, creating, or non-existent environments
                    <Button
                        variant="contained"
                        size={compact ? 'small' : 'medium'}
                        onClick={handleOpenSandbox}
                        startIcon={isDeploying ? (
                            <CircularProgress size={compact ? 16 : 20} color="inherit" />
                        ) : (
                            <CodeIcon size={compact ? 16 : 20} />
                        )}
                        disabled={isDeploying}
                        sx={{
                            bgcolor: 'warning.main',
                            '&:hover': {
                                bgcolor: 'warning.dark'
                            },
                            '&.Mui-disabled': {
                                bgcolor: alpha('#FF5722', 0.3)
                            }
                        }}
                    >
                        {isDeploying
                            ? t('sandbox_assignment.buttons.preparing_sandbox')
                            : t('sandbox_assignment.buttons.open_sandbox')}
                    </Button>
                )}
            </Box>

            {/* Deadline Info */}
            {assignment?.deadline && (
                <Alert
                    severity="info"
                    sx={{
                        mt: 2,
                        '& .MuiAlert-icon': {
                            color: 'warning.main'
                        }
                    }}
                >
                    {t('sandbox_assignment.deadline', {
                        date: new Date(assignment.deadline).toLocaleDateString()
                    })}
                </Alert>
            )}
        </Box>
    );

    if (hideContainer) {
        return content;
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
            }}
        >
            {content}
        </Paper>
    );
};

export default SandboxAssignment;
